 
import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import {
    DEVELOPMENT_EXPANDABLE_SECTIONS,
    FORECAST_METRIC_KEY,
    FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING,
    HIDE_WARNINGS_KEY,
    SELECTED_SCENARIO_KEY,
    TOTAL_ANTICIPATED_COST,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import {
    NSButton, NSSelect,
} from 'bricks';
import NSInfoBanner from 'components/NSInfoBanner/NSInfoBanner';
import {
    BOEProFormaDiscrepancies,
    IProFormaLocalStorage, IProFormaPageLocalStorage, IUnitTypeOption,
} from 'views/ProFormaTable/types';
import { getProFormaDevelopmentUnitTypeOptions, checkUsesSourcesUnequal } from 'views/ProFormaTable/helpers';
import { isLandDevelopmentModel } from 'helpers/proForma';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import useToast from 'hooks/useToast';
import { IOptionAnyValue } from 'bricks/types';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import UsesVsSourcesCard from './UsesVsSourcesCard/UsesVsSourcesCard';
import DevelopmentSourcesTable from './DevelopmentSourcesTable/DevelopmentSourcesTable';
import ImportUsesModal from './ImportUsesModal/ImportUsesModal';
import { useProFormaSocketContext } from '../socketContext/ProFormaSocketProvider';
import NewProjectActualsSnapshotModal from './NewProjectActualsSnapshotModal/NewProjectActualsSnapshotModal';
import './ProFormaDevelopment.scss';
import DevelopmentUsesImportSelection from './DevelopmentUsesImportSelection/DevelopmentUsesImportSelection';
import ProjectSourcesActualsTable from './ProjectSourcesActualsTable/ProjectSourcesActualsTable';
import { UsesTableActualsProvider } from './ProjectUsesActualsTable/context/UsesTableActualsProvider';
import ProjectUsesActualsTable from './ProjectUsesActualsTable/ProjectUsesActualsTable';
import BOEDiscrepancyIcon from '../../BOEDiscrepancyIcon/BOEDiscrepancyIcon';

export interface IProFormaDevelopmentProps {
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    hasWriteAccessToProForma: boolean;
    signUpMode?: boolean;
    boeDiscrepancies?: BOEProFormaDiscrepancies;
}

const ProFormaDevelopment = ({
    navigateToSection, hasWriteAccessToProForma,
    signUpMode = false, boeDiscrepancies,
}: IProFormaDevelopmentProps) => {
    const {
        uses, sources, proForma, handleDeleteBudgetClassification,
        isBudgetClassificationDeletionLoading, shouldUseProjectActuals,
        getLocalStorageValueByKey, projectActuals, projectActualsError,
        isLoadingProjectActuals, selectedForecastMetric, setSelectedForecastMetric,
    } = useProFormaSocketContext();

    const [usesMethod, setUsesMethod] = useState<string | null>(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedSnapshot, setSelectedSnapshot] = useState<number | null>(null);
    const [showNewSnapshotModal, setShowNewSnapshotModal] = useState(false);
    const [proFormaPageLocalStorage, setProFormaPageLocalStorage] = useLocalStorage<IProFormaPageLocalStorage>(PageSpecificKeys.ProFormaPage, {});
    const [unitType, setUnitType] = useState<IUnitTypeOption | null>(null);

    const currentProFormaLocalStorage = proForma.id in proFormaPageLocalStorage ? proFormaPageLocalStorage[proForma.id] : {} as IProFormaLocalStorage;
    const localStorageForecastMetric = FORECAST_METRIC_KEY in currentProFormaLocalStorage && currentProFormaLocalStorage[FORECAST_METRIC_KEY];
    const localStorageHideBOEWarnings = Boolean(getLocalStorageValueByKey(HIDE_WARNINGS_KEY));

    const { showError } = useToast();

    const selectedScenario = getLocalStorageValueByKey(SELECTED_SCENARIO_KEY)
        ? Number(getLocalStorageValueByKey(SELECTED_SCENARIO_KEY)) : null;

    const unitTypeOptions = getProFormaDevelopmentUnitTypeOptions(proForma.proFormaModelId);
    const existingUnitType = currentProFormaLocalStorage.developmentUnitType && unitTypeOptions.length
        ? unitTypeOptions.filter(option => option.value === currentProFormaLocalStorage.developmentUnitType)[0]
        : unitTypeOptions?.[0];

    const toggleImportModal = () => setIsImportModalOpen(!isImportModalOpen);

    const toggleNewSnapshotModal = () => setShowNewSnapshotModal(!showNewSnapshotModal);

    const handleCreateSnapshot = (name: string) => {
        // handleCreateSnapshot(name); // TODO: Implement this https://app.clickup.com/t/2326533/PRODUCT-4521
        toggleNewSnapshotModal();
    };

    const updateLocalStorageByKey = (key: string, value: any) => {
        const updatedCurrentProForma = { ...currentProFormaLocalStorage, [key]: value };
        const updatedProFormaStorage = { ...proFormaPageLocalStorage, [proForma.id]: updatedCurrentProForma };
        setProFormaPageLocalStorage(updatedProFormaStorage as IProFormaPageLocalStorage);
    };

    const handleSelectForecastMetric = (forecastMetric: IOptionAnyValue) => {
        setSelectedForecastMetric(forecastMetric);
        updateLocalStorageByKey(FORECAST_METRIC_KEY, forecastMetric.value);
    };

    const deleteSuccessCallback = () => {
        setUsesMethod(null);
        toggleImportModal();
    };

    const handleDeleteBudgetCategories = () => {
        handleDeleteBudgetClassification([], true, deleteSuccessCallback);
    };

    if (!proForma) {
        // show loading screen instead
        return null;
    }

    const resetSnapshot = () => {
        setSelectedSnapshot(null);
        handleSelectForecastMetric(FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING[0]);
    };

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        let forecastMetric = FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING[0];
        if (localStorageForecastMetric) {
            forecastMetric = FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING.find(
                option => option.value === localStorageForecastMetric,
            ) || FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING[0];
        }
        setSelectedForecastMetric(forecastMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localStorageForecastMetric]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (projectActualsError) {
            showError('Error getting project actuals. Please try again.');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectActualsError]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (!unitType && existingUnitType) {
            setUnitType(existingUnitType);
        }
    }, [unitType, existingUnitType]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (unitType) {
            const updatedProFormaStorage = {
                ...proFormaPageLocalStorage,
                [proForma.id]: { ...currentProFormaLocalStorage, developmentUnitType: unitType.value },
            };
            setProFormaPageLocalStorage(updatedProFormaStorage as IProFormaPageLocalStorage);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitType]);

    return (
        <div className="ProFormaDevelopment">
            <RenderIf isTrue={checkUsesSourcesUnequal(uses, sources) && !shouldUseProjectActuals}>
                <NSInfoBanner
                    title="Warning: Total uses and total sources are not equal"
                    subtitle="Several Pro Forma features and calculations will not be available until these values are equal."
                    className="mt-3 mb-0 NSCard__left-border--warning ProFormaDevelopment__warning-banner"
                    warning
                    customInfo={(
                        <UsesVsSourcesCard
                            usesTotal={uses?.totals?.amount || 0}
                            sourcesTotal={sources?.totals?.amount || 0}
                        />
                    )}
                />
            </RenderIf>

            <ExpandableSection
                number={DEVELOPMENT_EXPANDABLE_SECTIONS[0].number}
                title={DEVELOPMENT_EXPANDABLE_SECTIONS[0].title}
                subtitle={DEVELOPMENT_EXPANDABLE_SECTIONS[0].subtitle}
                className="mt-3 NSCard--level-2"
                warningIcon={(
                    <BOEDiscrepancyIcon
                        section={DEVELOPMENT_EXPANDABLE_SECTIONS[0].title}
                        metricNames={boeDiscrepancies ? boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[0].number] : []}
                        className="mr-1 my-auto"
                    />
                )}
                showWarningIcon={boeDiscrepancies && !localStorageHideBOEWarnings ?
                    !!boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[0].number]?.length :
                    false}
                displaySectionButtons
                sectionButtons={(
                    <>
                        <RenderIf isTrue={hasWriteAccessToProForma && !!uses?.uses?.length}>
                            <NSButton
                                text="Automatically add detailed budget"
                                color="primary"
                                outline
                                className="mr-2"
                                callback={toggleImportModal}
                            />
                        </RenderIf>

                        <RenderIf
                            isTrue={
                                !hasWriteAccessToProForma
                                && shouldUseProjectActuals
                                && proForma.subprojectId
                                && !(projectActuals?.projectActuals?.uses?.isEarlyPlanning || false)
                            }
                        >
                            <div className="d-flex pr-1">
                                <RenderIf isTrue={Boolean(selectedSnapshot)}>
                                    <NSButton
                                        text="Reset"
                                        className="text-primary mr-1"
                                        callback={resetSnapshot}
                                    />
                                </RenderIf>
                                {/* TODO keep this code until we resume this functionality */}
                                {/* <div className="ProFormaDevelopment__expandable-section-button--border-right pr-1">
                                    <UsesSnapshotSelect
                                        selectedSnapshot={selectedSnapshot}
                                        setSelectedSnapshot={setSelectedSnapshot}
                                        toggleNewSnapshotModal={toggleNewSnapshotModal}
                                    />
                                </div> */}
                            </div>
                            <div className="mr-1">
                                <NSSelect
                                    name="forecastMetricSelect"
                                    isClearable={false}
                                    options={FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING}
                                    onChange={(option: IOptionAnyValue) => handleSelectForecastMetric(option)}
                                    value={selectedForecastMetric}
                                    menuPortalTarget={document.body}
                                    menuPosition="absolute"
                                    styles={{
                                        ...customStyles,
                                        menuPortal: (provided: any) => ({
                                            ...provided,
                                            zIndex: 10,
                                        }),
                                    }}
                                />
                            </div>
                        </RenderIf>
                    </>
                )}
                isExpanded
                animate
            >
                <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma
                    || (!isLandDevelopmentModel(proForma.proFormaModelId) && !proForma.builtGrossSquareFootage)}>
                    <DevelopmentUsesImportSelection
                        navigateToSection={navigateToSection}
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                        usesMethod={usesMethod}
                        setUsesMethod={setUsesMethod}
                        unitType={unitType}
                        setUnitType={setUnitType}
                        unitTypeOptions={unitTypeOptions}
                        signUpMode={signUpMode}
                    />
                    <RenderIf
                        isTrue={shouldUseProjectActuals && projectActuals && !isEmpty(projectActuals)}
                        isLoading={isLoadingProjectActuals && !projectActuals}
                    >
                        <div className="p-3">
                            <UsesTableActualsProvider
                                navigateToSection={navigateToSection}
                                forecastMetric={selectedForecastMetric?.value || TOTAL_ANTICIPATED_COST}
                                selectedScenario={selectedScenario}
                                isEarlyPlanningProject={projectActuals?.projectActuals?.uses?.isEarlyPlanning || false}
                            >
                                <ProjectUsesActualsTable
                                    uses={projectActuals?.projectActuals?.uses?.budgetClassifications || []}
                                />
                            </UsesTableActualsProvider>
                        </div>
                    </RenderIf>
                </ReadOnlyWrapper>
            </ExpandableSection>
            <ExpandableSection
                number={DEVELOPMENT_EXPANDABLE_SECTIONS[1].number}
                title={DEVELOPMENT_EXPANDABLE_SECTIONS[1].title}
                subtitle={DEVELOPMENT_EXPANDABLE_SECTIONS[1].subtitle}
                className="mt-3 NSCard--level-2"
                warningIcon={(
                    <BOEDiscrepancyIcon
                        section={DEVELOPMENT_EXPANDABLE_SECTIONS[1].title}
                        metricNames={boeDiscrepancies ? boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[1].number] : []}
                        className="mr-1 my-auto"
                    />
                )}
                showWarningIcon={boeDiscrepancies && !localStorageHideBOEWarnings ?
                    !!boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[1].number]?.length :
                    false}
                isExpanded
                animate
            >
                <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
                    <RenderIf isTrue={!isEmpty(sources) && unitType && !shouldUseProjectActuals}>
                        <DevelopmentSourcesTable
                            sources={sources}
                            unitType={unitType!}
                            setUnitType={setUnitType}
                            unitTypeOptions={unitTypeOptions}
                            hasWriteAccessToProForma={hasWriteAccessToProForma}
                        />
                    </RenderIf>

                    <RenderIf
                        isTrue={shouldUseProjectActuals && projectActuals && !isEmpty(projectActuals)}
                        isLoading={isLoadingProjectActuals && !projectActuals}
                    >
                        <ProjectSourcesActualsTable
                            sources={projectActuals?.projectActuals?.sources}
                        />
                    </RenderIf>
                </ReadOnlyWrapper>
            </ExpandableSection>

            <RenderIf isTrue={isImportModalOpen}>
                <ImportUsesModal
                    isOpen={isImportModalOpen}
                    toggle={toggleImportModal}
                    handleDeleteBudgetCategories={handleDeleteBudgetCategories}
                    isLoading={isBudgetClassificationDeletionLoading}
                />
            </RenderIf>

            <RenderIf isTrue={showNewSnapshotModal}>
                <NewProjectActualsSnapshotModal
                    isOpen={showNewSnapshotModal}
                    toggle={toggleNewSnapshotModal}
                    handleCreateSnapshot={handleCreateSnapshot}
                    isLoadingCallback={false} // TODO: Implement this https://app.clickup.com/t/2326533/PRODUCT-4521
                    forecastMetric={selectedForecastMetric.label}
                />
            </RenderIf>
        </div>
    );
};

export default ProFormaDevelopment;
